/**
 * Spawner — the RUN-AND-GUN dispenser (step 4). The genre's "kill-the-source" objective enemy.
 *
 * A STATIC emitter that spawns a grunt (`props.spawnType`, default GOOMBA) at its base on an
 * integer cadence, up to a live cap (`props.maxAlive`, default 3). It keeps refs to the entities
 * it spawned (spawnEntityAt RETURNS them) and counts the live ones, so it stops feeding while its
 * brood is at cap and resumes as they die — destroy the spawner to end the flow. UN-stompable +
 * contact-hurts; only a bullet kills it, soaking `props.hp` (default 4) via the shared seam.
 * Reuses (c) the runtime spawn queue + (e) hp.
 *
 * Determinism: integer counters + the deterministic spawn queue; the children's alive-state is
 * itself deterministic → the cap gate is replay-safe. No random / wall-clock / transcendentals.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const SQUASH_SECONDS = 0.4;
const DEFAULT_COOLDOWN = 150;
const DEFAULT_MAX_ALIVE = 3;
const DEFAULT_HP = 4;
const OPEN_STEPS = 14;
/** POLISH_03 #5: hard upper bounds so a level can't publish an unbounded-growth perf bomb
 *  (an uncapped maxAlive/hp compounds with Sim.entities' four per-step passes). */
const MAX_ALIVE_CAP = 8;
const HP_CAP = 99;

/** The grunt types a spawner may emit (an allow-list — a spawner can't spawn a block/hazard). */
const SPAWN_TYPES: Record<string, EntityType> = {
  GOOMBA: EntityType.GOOMBA,
  WALKER: EntityType.WALKER,
  GUNNER: EntityType.GUNNER,
  FLYER: EntityType.FLYER,
};

export class Spawner implements Entity {
  readonly type = EntityType.SPAWNER;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;
  hp: number;
  squashTimer = 0;

  private cooldown: number;
  private maxAlive: number;
  private spawnType: EntityType;
  private spawnProps: Record<string, unknown>;
  private sinceSpawn = 0;
  private openTimer = 0;
  private children: Entity[] = [];

  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 56, h = 52) {
    this.body = { x: cx - w / 2, y: feetY - h, w, h };
    this.cooldown = typeof props?.spawnCooldownSteps === 'number'
      ? Math.max(20, Math.floor(props.spawnCooldownSteps)) : DEFAULT_COOLDOWN;
    this.maxAlive = typeof props?.maxAlive === 'number'
      ? Math.min(MAX_ALIVE_CAP, Math.max(1, Math.floor(props.maxAlive))) : DEFAULT_MAX_ALIVE;
    this.hp = typeof props?.hp === 'number'
      ? Math.min(HP_CAP, Math.max(1, Math.floor(props.hp))) : DEFAULT_HP;
    const st = typeof props?.spawnType === 'string' ? props.spawnType.toUpperCase() : 'GOOMBA';
    this.spawnType = SPAWN_TYPES[st] ?? EntityType.GOOMBA;
    this.spawnProps = (props?.spawnProps && typeof props.spawnProps === 'object')
      ? props.spawnProps as Record<string, unknown> : {};
  }

  private aliveChildren(): number {
    let n = 0;
    for (const c of this.children) if (c.alive) n++;
    return n;
  }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) { this.squashTimer = Math.max(0, this.squashTimer - dt); return; }
    if (this.openTimer > 0) this.openTimer--;
    if (this.aliveChildren() < this.maxAlive) {
      this.sinceSpawn++;
      if (this.sinceSpawn >= this.cooldown) { this.emit(ctx); this.sinceSpawn = 0; }
    } else {
      this.sinceSpawn = 0; // hold while the brood is at cap (don't bank a burst)
    }
  }

  private emit(ctx: EntityContext): void {
    const cx = this.body.x + this.body.w / 2;
    const feetY = this.body.y + this.body.h; // emit at the spawner's base
    const e = ctx.spawnEntityAt(this.spawnType, cx, feetY, this.spawnProps);
    if (e) this.children.push(e);
    this.openTimer = OPEN_STEPS;
    ctx.playSound('bump', 0.3, 0.8); // POLISH_01 #1 — mechanical spawn clank
  }

  onPlayerOverlap(ctx: EntityContext): void { ctx.hurtPlayer(); } // un-stompable

  damageable(): boolean { return true; }                          // bullet-killable (seam chips hp)
  onHit(ctx: EntityContext): void { ctx.playSound('bump', 0.3, 1.0); } // POLISH_01 #1 — hull clink

  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
  }

  spriteKey(): string {
    if (!this.alive) return 'spawner_rest';
    return this.openTimer > 0 ? 'spawner_open' : 'spawner_rest';
  }
  visible(): boolean { return this.alive || this.squashTimer > 0; }
}
